import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Mail, Wand2 } from 'lucide-react';
import { useAuth } from '@context/AuthContext';
import { ROUTES, API_ENDPOINTS } from '@utils/constants';
import Layout from '@components/layout/Layout';
import Button from '@components/common/Button';
import Input from '@components/common/Input';
import Alert from '@components/common/Alert';
import DemoLoginButton from '@components/auth/DemoLoginButton';
import api from '@services/api';
import { useRecaptcha } from '@hooks/useRecaptcha';

const LoginPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [oauthProviders, setOauthProviders] = useState<string[]>([]);

  const { isAuthenticated, error } = useAuth();
  const { getToken } = useRecaptcha();
  const navigate = useNavigate();
  const location = useLocation();

  // Get the intended destination from location state
  const from = location.state?.from?.pathname || ROUTES.HOME;

  // Fetch available OAuth providers on mount
  useEffect(() => {
    const fetchProviders = async () => {
      try {
        const response = await api.get(API_ENDPOINTS.AUTH_OAUTH_PROVIDERS);
        setOauthProviders(response.data.providers || []);
      } catch {
        // OAuth not configured, that's fine
        setOauthProviders([]);
      }
    };
    fetchProviders();
  }, []);

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, navigate, from]);

  const handleEmailChange = (value: string) => {
    setEmail(value);
    setFormError(null);
    setSuccessMessage(null);
  };

  const requestMagicLink = async (targetEmail: string) => {
    setIsLoading(true);
    setFormError(null);

    try {
      const captchaToken = await getToken('magic_link_request');
      await api.post(API_ENDPOINTS.AUTH_MAGIC_LINK_REQUEST, {
        email: targetEmail,
        captcha_token: captchaToken,
      });
      setSuccessMessage(
        'Check your email! We sent you a magic link to sign in. The link expires in 15 minutes.'
      );
    } catch {
      // Always show success to prevent email enumeration
      setSuccessMessage(
        'If this email is registered, a login link has been sent. Check your inbox.'
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email || !emailRegex.test(email)) {
      setFormError('Please enter a valid email address');
      return;
    }

    await requestMagicLink(email);
  };

  const handleDemoLogin = (demoEmail: string) => {
    setEmail(demoEmail);
    requestMagicLink(demoEmail);
  };

  const handleOAuthLogin = async (provider: 'google') => {
    try {
      const response = await api.get(API_ENDPOINTS.AUTH_OAUTH_GOOGLE);
      const { authorization_url } = response.data;

      // Redirect to OAuth provider
      window.location.href = authorization_url;
    } catch {
      setFormError(`Failed to initiate ${provider} login`);
    }
  };

  return (
    <Layout>
      <div className="min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          {/* Header */}
          <div className="text-center">
            <h2 className="text-3xl font-bold text-gray-900 mb-2">Welcome back</h2>
            <p className="text-gray-600">Sign in to your Pixel Share account</p>
          </div>

          {/* Login Form */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
            {/* Google OAuth */}
            <Button
              variant="outline"
              className="w-full"
              onClick={() => handleOAuthLogin('google')}
              disabled={!oauthProviders.includes('google')}
            >
              <svg className="w-5 h-5" viewBox="0 0 20 20">
                <path
                  fill="currentColor"
                  d="M15.545 6.558a9.42 9.42 0 0 1 .139 1.626c0 2.434-.87 4.492-2.384 5.885h.002C11.978 15.292 10.158 16 8 16A8 8 0 1 1 8 0a7.689 7.689 0 0 1 5.352 2.082l-2.284 2.284A4.347 4.347 0 0 0 8 3.166c-2.087 0-3.86 1.408-4.492 3.304a4.792 4.792 0 0 0 0 3.063h.003c.635 1.893 2.405 3.301 4.492 3.301 1.078 0 2.004-.276 2.722-.764h-.003a3.702 3.702 0 0 0 1.599-2.431H8v-3.08h7.545z"
                />
              </svg>
              <span className="ml-2">Continue with Google</span>
            </Button>
            {!oauthProviders.includes('google') && (
              <p className="text-xs text-gray-400 text-center mt-2">
                Google sign-in not configured
              </p>
            )}

            {/* Divider */}
            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white text-gray-500">Or sign in with email</span>
              </div>
            </div>

            {/* Magic Link Info */}
            <div className="flex items-center justify-center mb-6 text-primary-600">
              <Wand2 size={20} className="mr-2" />
              <span className="text-sm font-medium">Passwordless Sign In</span>
            </div>

            {successMessage && (
              <Alert
                type="success"
                message={successMessage}
                className="mb-4"
                dismissible
                onDismiss={() => setSuccessMessage(null)}
              />
            )}

            <form className="space-y-6" onSubmit={handleSubmit}>
              <Input
                type="email"
                label="Email Address"
                value={email}
                onChange={e => handleEmailChange(e.target.value)}
                leftIcon={<Mail size={16} />}
                placeholder="Enter your email"
                required
                autoComplete="email"
              />

              {(formError || error) && (
                <Alert
                  type="error"
                  message={formError || error || ''}
                  dismissible
                  onDismiss={() => setFormError(null)}
                />
              )}

              <p className="text-sm text-gray-500 text-center">
                We'll send you a secure link to sign in - no password needed!
              </p>

              <Button
                type="submit"
                variant="primary"
                size="large"
                className="w-full"
                loading={isLoading}
                disabled={isLoading}
              >
                Send Magic Link
              </Button>
            </form>

            <DemoLoginButton onDemoLogin={handleDemoLogin} />
          </div>

          {/* Sign up link */}
          <div className="text-center">
            <p className="text-gray-600">
              Don't have an account?{' '}
              <Link
                to={ROUTES.REGISTER}
                className="font-medium text-primary-600 hover:text-primary-500"
              >
                Sign up for free
              </Link>
            </p>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default LoginPage;
